import { LoginFormData, SignupFormData } from "@/features/auth/schemas/auth";
import { supabase } from "@lezz/supabase";
import { useMutation } from "@tanstack/react-query";
import { logger } from "@/shared/lib/logger";
import { posthog } from "@/shared/lib/posthog";

export const useLogin = () => {
  return useMutation({
    mutationFn: async (data: LoginFormData) => {
      const { data: session, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        logger.warn("Login failed", {
          reason: error.message,
          authMethod: "password",
        });
        throw error;
      }

      logger.info("User logged in", {
        userId: session.user?.id,
        authMethod: "password",
      });

      if (session.user?.id) {
        posthog?.identify(session.user.id, {
          $set: { email: session.user.email ?? null },
          $set_once: { first_login_date: new Date().toISOString() },
        });
        posthog?.capture("user_logged_in", { auth_method: "password" });
      }

      return session;
    },
  });
};

export const useSignup = () => {
  return useMutation({
    mutationFn: async (data: SignupFormData) => {
      const { data: authData, error: signUpError } = await supabase.auth.signUp(
        {
          email: data.email,
          password: data.password,
          options: {
            data: { opt_in_marketing: data.optInMarketing ?? false },
          },
        }
      );

      if (signUpError) {
        logger.warn("Signup failed", {
          reason: signUpError.message,
        });
        throw signUpError;
      }

      logger.info("User signed up", {
        userId: authData.user?.id,
      });

      if (authData.user?.id) {
        posthog?.identify(authData.user.id, {
          $set: { email: authData.user.email ?? null },
          $set_once: { signup_date: new Date().toISOString() },
        });
        posthog?.capture("user_signed_up", {
          auth_method: "password",
          opt_in_marketing: data.optInMarketing ?? false,
        });
      }

      return authData;
    },
  });
};
